import { useState, useCallback } from "react";

/**
 * Custom hook to handle booking conflict errors across the application
 * Provides state management and error handling for booking conflicts
 *
 * @param {Object} options - Configuration options
 * @param {string} options.titleKey - Translation key for modal title (default: "crewMember.scheduledTimeOff.bookingConflictList.title")
 * @param {string} options.messageKey - Translation key for modal message (default: "crewMember.scheduledTimeOff.bookingConflictList.message")
 * @param {string} options.closeButtonKey - Translation key for close button (default: "crewMember.scheduledTimeOff.buttons.close")
 *
 * @returns {Object} Hook return object containing:
 *   - bookingConflictOpen: boolean - Whether the conflict modal is open
 *   - conflictBookings: array - Array of conflicting bookings
 *   - conflictCrewName: string - Name of the crew member with conflicts
 *   - handleBookingConflictError: function - Error handler to check and process booking conflicts
 *   - closeBookingConflictModal: function - Handler to close the modal
 *   - bookingConflictModalProps: object - Props to spread on BookingConflictModal component
 */
export function useBookingConflict(options = {}) {
  const {
    titleKey = "crewMember.scheduledTimeOff.bookingConflictList.title",
    messageKey = "crewMember.scheduledTimeOff.bookingConflictList.message",
    closeButtonKey = "crewMember.scheduledTimeOff.buttons.close"
  } = options;

  const [bookingConflictOpen, setBookingConflictOpen] = useState(false);
  const [conflictBookings, setConflictBookings] = useState([]);
  const [conflictCrewName, setConflictCrewName] = useState("");

  /**
   * Handles booking conflict errors by checking if the error contains conflicting bookings
   * If conflicts are found, sets the state and throws a marked error
   * If no conflicts, re-throws the original error
   *
   * @param {Error} error - The error object from the API call
   * @throws {Error} Always throws - either a marked booking conflict error or the original error
   */
  const handleBookingConflictError = useCallback((error) => {
    // Check for booking conflicts - API returns conflicting_bookings array
    if (error?.data?.conflicting_bookings?.length > 0) {
      setConflictBookings(error.data.conflicting_bookings);
      setConflictCrewName(error.data.conflicting_for || "");
      setBookingConflictOpen(true);

      const conflictError = new Error("Booking conflict");
      conflictError.isBookingConflict = true;
      throw conflictError;
    }

    // Re-throw the original error if it's not a booking conflict
    throw error;
  }, []);

  /**
   * Closes the booking conflict modal and resets conflict state
   */
  const closeBookingConflictModal = useCallback(() => {
    setBookingConflictOpen(false);
    setConflictBookings([]);
    setConflictCrewName("");
  }, []);

  /**
   * Props object to spread on BookingConflictModal component
   * Includes all necessary props for the modal
   */
  const bookingConflictModalProps = {
    open: bookingConflictOpen,
    onCancel: closeBookingConflictModal,
    bookings: conflictBookings,
    crewMemberName: conflictCrewName,
    titleKey,
    messageKey,
    closeButtonKey
  };

  return {
    bookingConflictOpen,
    conflictBookings,
    conflictCrewName,
    handleBookingConflictError,
    closeBookingConflictModal,
    bookingConflictModalProps
  };
}
